import { useEffect, useState } from 'react'
import { adminApi } from '../lib/api'
import { transformEvents } from '../lib/transformers'
import { Plus, Trash2, Edit } from 'lucide-react'
import type { Event } from '../types'
import EventFormModal from '../components/EventFormModal'
import Pagination from '../components/Pagination'
import { useToast } from '../components/ui/ToastProvider'

export default function EventsPage() {
  const { showToast } = useToast()
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [showEventModal, setShowEventModal] = useState(false)
  const [editingEvent, setEditingEvent] = useState<Event | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const itemsPerPage = 20

  useEffect(() => {
    loadEvents()
  }, [currentPage])

  const loadEvents = async () => {
    setLoading(true)
    try {
      const response = await adminApi.getEvents({
        page: currentPage,
        limit: itemsPerPage
      })
      let rawData = []
      let pagination = null

      if (!response.data) {
        rawData = []
      } else if (Array.isArray(response.data)) {
        rawData = response.data
      } else if (typeof response.data === 'object') {
        // API returns {success: true, data: {events: [], pagination: {}}}
        rawData = response.data?.data?.events || response.data?.events || response.data?.data || response.data?.items || []
        pagination = response.data?.data?.pagination || response.data?.pagination
      }

      // Transform snake_case to camelCase
      const data = transformEvents(Array.isArray(rawData) ? rawData : [])
      console.log('Loaded events sample:', data[0]) // Debug: check date format
      setEvents(data)

      // Update pagination info
      if (pagination) {
        setTotalPages(pagination.totalPages || Math.ceil(pagination.total / itemsPerPage) || 1)
        setTotalItems(pagination.total || rawData.length)
      } else {
        // If no pagination info, assume single page
        setTotalPages(1)
        setTotalItems(rawData.length)
      }
    } catch (error) {
      console.error('Failed to load events:', error)
      setEvents([])
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this event?')) return

    try {
      await adminApi.deleteEvent(id)
      loadEvents()
    } catch (error) {
      console.error('Failed to delete event:', error)
    }
  }

  const handleEdit = (event: Event) => {
    setEditingEvent(event)
    setShowEventModal(true)
  }

  const handleCreate = () => {
    setEditingEvent(null)
    setShowEventModal(true)
  }

  const handleSaveEvent = async (data: any) => {
    try {
      if (editingEvent) {
        await adminApi.updateEvent(editingEvent.id, data)
      } else {
        // For creating events, we need a userId. Let's use a default admin user or get from somewhere
        // For now, we'll assume we have a way to get the current admin's userId
        const currentAdmin = await adminApi.getMe()
        await adminApi.createEvent(data, currentAdmin.data.userId)
      }
      await loadEvents()
      setShowEventModal(false)
      setEditingEvent(null)
    } catch (error) {
      console.error('Failed to save event:', error)
      showToast('Failed to save event. Please try again.', { type: 'error' })
    }
  }

  const handleCloseModal = () => {
    setShowEventModal(false)
    setEditingEvent(null)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Events</h1>
          <p className="text-gray-600 mt-1">Manage events and create official content</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={handleCreate}>
          <Plus className="h-4 w-4" />
          Create Event
        </button>
      </div>

      <div className="card">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : events.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No events found</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 font-medium text-gray-700">Cover</th>
                    <th className="pb-3 font-medium text-gray-700">Title</th>
                    <th className="pb-3 font-medium text-gray-700">Organizer</th>
                    <th className="pb-3 font-medium text-gray-700">Status</th>
                    <th className="pb-3 font-medium text-gray-700">Date</th>
                    <th className="pb-3 font-medium text-gray-700">Attendees</th>
                    <th className="pb-3 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-3">
                        {event.coverImage ? (
                          <img
                            src={event.coverImage}
                            alt={event.title}
                            className="h-12 w-20 object-cover rounded"
                          />
                        ) : (
                          <div className="h-12 w-20 bg-gray-100 rounded flex items-center justify-center text-xs text-gray-400">
                            No image
                          </div>
                        )}
                      </td>
                      <td className="py-3">
                        <div>
                          <p
                            className="font-medium text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                            onClick={() => handleEdit(event)}
                          >
                            {event.title}
                          </p>
                          <p className="text-sm text-gray-600">{event.location?.address || 'No location'}</p>
                        </div>
                      </td>
                      <td className="py-3 text-sm text-gray-700">{event.userName}</td>
                      <td className="py-3">
                        <span className={`badge ${
                          event.status === 'upcoming' ? 'badge-info' :
                          event.status === 'ongoing' ? 'badge-success' :
                          event.status === 'completed' ? 'badge-info' :
                          'badge-danger'
                        }`}>
                          {event.status}
                        </span>
                      </td>
                      <td className="py-3 text-sm text-gray-700">
                        {new Date(event.startDate).toLocaleDateString()}
                      </td>
                      <td className="py-3 text-sm text-gray-700">{event.attendees}</td>
                      <td className="py-3">
                        <div className="flex gap-2">
                          <button
                            className="px-3 py-1.5 bg-gray-100 hover:bg-blue-50 text-blue-600 rounded-lg text-sm font-medium transition-colors"
                            onClick={() => handleEdit(event)}
                          >
                            <Edit className="h-4 w-4 inline mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(event.id)}
                            className="px-3 py-1.5 bg-gray-100 hover:bg-red-50 text-red-600 rounded-lg text-sm font-medium transition-colors"
                          >
                            <Trash2 className="h-4 w-4 inline mr-1" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
            />
          </>
        )}
      </div>

      {showEventModal && (
        <EventFormModal
          event={editingEvent}
          onClose={handleCloseModal}
          onSave={handleSaveEvent}
        />
      )}
    </div>
  )
}
